package fr.manyverse.app.presentation.viewmodel

import fr.manyverse.app.domain.model.Character
import fr.manyverse.app.domain.model.CharacterSheet
import fr.manyverse.app.domain.model.CharacterToCreate
import fr.manyverse.app.domain.model.ImageData
import fr.manyverse.app.domain.model.PreMadeCharacter
import fr.manyverse.app.domain.usecase.CopyPreMadeToCharacterUseCase
import fr.manyverse.app.domain.usecase.CountCharactersInUniverseUseCase
import fr.manyverse.app.domain.usecase.CountPreMadeCharactersUseCase
import fr.manyverse.app.domain.usecase.CreateCharacterUseCase
import fr.manyverse.app.domain.usecase.GetCharacterByIdUseCase
import fr.manyverse.app.domain.usecase.GetCharactersUseCase
import fr.manyverse.app.domain.usecase.GetPreMadeCharactersUseCase
import fr.manyverse.app.domain.usecase.NotifyPlayerJoinedUseCase
import fr.manyverse.app.domain.usecase.SetTableMemberCharacterUseCase
import fr.manyverse.app.domain.usecase.TouchMemberCharacterUseCase
import fr.manyverse.app.domain.usecase.UpdateCharacterSheetUseCase
import fr.manyverse.app.domain.usecase.UpdateCharacterUseCase
import fr.manyverse.app.domain.usecase.UploadCharacterImageUseCase
import fr.manyverse.app.presentation.error.UserErrorNotifier
import fr.manyverse.app.presentation.state.AsyncUiState
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.async
import kotlinx.coroutines.coroutineScope

class TableCharacterViewModel(
    private val tableId: String,
    private val userId: String,
    private val universeId: String,
    private val getCharactersUseCase: GetCharactersUseCase,
    private val createCharacterUseCase: CreateCharacterUseCase,
    private val uploadCharacterImageUseCase: UploadCharacterImageUseCase,
    private val updateCharacterUseCase: UpdateCharacterUseCase,
    private val getCharacterByIdUseCase: GetCharacterByIdUseCase,
    private val setTableMemberCharacterUseCase: SetTableMemberCharacterUseCase,
    private val touchMemberCharacterUseCase: TouchMemberCharacterUseCase,
    private val notifyPlayerJoinedUseCase: NotifyPlayerJoinedUseCase,
    private val getPreMadeCharactersUseCase: GetPreMadeCharactersUseCase,
    private val copyPreMadeToCharacterUseCase: CopyPreMadeToCharacterUseCase,
    private val updateCharacterSheetUseCase: UpdateCharacterSheetUseCase,
    private val countCharactersInUniverseUseCase: CountCharactersInUniverseUseCase,
    private val countPreMadeCharactersUseCase: CountPreMadeCharactersUseCase,
    private val errorNotifier: UserErrorNotifier
) : BaseViewModel() {

    private val _choiceAvailability =
        MutableStateFlow<AsyncUiState<CharacterChoiceAvailability>>(AsyncUiState.Idle)
    val choiceAvailability: StateFlow<AsyncUiState<CharacterChoiceAvailability>> =
        _choiceAvailability.asStateFlow()

    private val _charactersState =
        MutableStateFlow<AsyncUiState<List<Character>>>(AsyncUiState.Idle)
    val charactersState: StateFlow<AsyncUiState<List<Character>>> = _charactersState.asStateFlow()

    private val _preMadesState =
        MutableStateFlow<AsyncUiState<List<PreMadeCharacter>>>(AsyncUiState.Idle)
    val preMadesState: StateFlow<AsyncUiState<List<PreMadeCharacter>>> = _preMadesState.asStateFlow()

    private val _assignState = MutableStateFlow<AsyncUiState<Unit>>(AsyncUiState.Idle)
    val assignState: StateFlow<AsyncUiState<Unit>> = _assignState.asStateFlow()

    private val _updateNameState = MutableStateFlow<AsyncUiState<Unit>>(AsyncUiState.Idle)
    val updateNameState: StateFlow<AsyncUiState<Unit>> = _updateNameState.asStateFlow()

    private val _updateImageState = MutableStateFlow<AsyncUiState<Unit>>(AsyncUiState.Idle)
    val updateImageState: StateFlow<AsyncUiState<Unit>> = _updateImageState.asStateFlow()

    private val _refreshedCharacter = MutableStateFlow<Character?>(null)
    val refreshedCharacter: StateFlow<Character?> = _refreshedCharacter.asStateFlow()

    private val _updateSheetState = MutableStateFlow<AsyncUiState<Unit>>(AsyncUiState.Idle)
    val updateSheetState: StateFlow<AsyncUiState<Unit>> = _updateSheetState.asStateFlow()

    /**
     * Vérifie, par deux requêtes de comptage parallèles, si le joueur peut choisir un personnage
     * existant ou un pré-tiré.
     *
     * Les deux résultats sont exposés dans un état unique afin que l'écran de choix ne fasse
     * qu'une seule transition visuelle, plutôt que d'activer ses boutons l'un après l'autre.
     * Aucun avatar n'est signé ici : le contenu des listes n'est chargé qu'à l'ouverture de la
     * boîte de dialogue correspondante.
     */
    fun loadChoiceAvailability() {
        if (_choiceAvailability.value.isPendingOrLoaded) return
        _choiceAvailability.value = AsyncUiState.Loading
        launchAsync(_choiceAvailability) {
            coroutineScope {
                val characters = async { countCharactersInUniverseUseCase(universeId, userId) }
                val preMades = async { countPreMadeCharactersUseCase(tableId) }
                val charactersCount = characters.await()
                val preMadesCount = preMades.await()

                val error = charactersCount.exceptionOrNull() ?: preMadesCount.exceptionOrNull()
                if (error != null) {
                    Result.failure(error)
                } else {
                    Result.success(
                        CharacterChoiceAvailability(
                            hasExistingCharacters = charactersCount.getOrDefault(0) > 0,
                            hasPreMades = preMadesCount.getOrDefault(0) > 0
                        )
                    )
                }
            }
        }
    }

    /** Charge la liste des personnages existants, sans avatars : la sélection se fait par nom. */
    fun loadExistingCharacters() {
        if (_charactersState.value.isPendingOrLoaded) return
        _charactersState.value = AsyncUiState.Loading
        launchAsync(_charactersState) {
            getCharactersUseCase(universeId, userId, withSignedAvatars = false)
        }
    }

    fun loadPreMades() {
        if (_preMadesState.value.isPendingOrLoaded) return
        _preMadesState.value = AsyncUiState.Loading
        launchAsync(_preMadesState) {
            getPreMadeCharactersUseCase(tableId).map { items ->
                items.sortedByDescending { it.createdAt }
            }
        }
    }

    fun createAndAssignCharacter(
        name: String,
        story: String,
        imageData: ImageData? = null
    ) {
        launchAsyncWithCallback(_assignState, {
            val character = createCharacterUseCase(
                universeId,
                CharacterToCreate(userId = userId, name = name, story = story)
            ).getOrElse { return@launchAsyncWithCallback Result.failure(it) }

            if (imageData != null) {
                // Le personnage existe déjà : l'image ratée ne bloque pas l'arrivée à la table, mais
                // le joueur doit le savoir pour la reprendre depuis son avatar
                uploadCharacterImageUseCase(userId, character.id, imageData)
                    .fold(
                        onSuccess = { imagePath -> updateCharacterUseCase(character.id, name, story, imagePath) },
                        onFailure = { Result.failure(it) }
                    )
                    .onFailure(errorNotifier::notify)
            }

            val result = setTableMemberCharacterUseCase(
                tableId = tableId,
                userId = userId,
                characterId = character.id
            )
            result.onSuccess {
                notifyPlayerJoinedUseCase(
                    tableId = tableId,
                    triggeredBy = userId,
                    characterName = name
                )
            }
            result
        }) {}
    }

    fun selectPreMade(preMadeId: String) {
        launchAsyncWithCallback(_assignState, {
            val character = copyPreMadeToCharacterUseCase(preMadeId, userId, universeId)
                .getOrElse { return@launchAsyncWithCallback Result.failure(it) }

            val result = setTableMemberCharacterUseCase(
                tableId = tableId,
                userId = userId,
                characterId = character.id
            )
            result.onSuccess {
                notifyPlayerJoinedUseCase(
                    tableId = tableId,
                    triggeredBy = userId,
                    characterName = character.name
                )
            }
            result
        }) {}
    }

    fun assignExistingCharacter(character: Character) {
        launchAction(_assignState) {
            val result = setTableMemberCharacterUseCase(
                tableId = tableId,
                userId = userId,
                characterId = character.id
            )
            result.onSuccess {
                notifyPlayerJoinedUseCase(
                    tableId = tableId,
                    triggeredBy = userId,
                    characterName = character.name
                )
            }
            result
        }
    }

    fun resetAssignState() = resetState(_assignState)

    fun updateCharacterName(character: Character, newName: String) {
        launchAsyncWithCallback(
            _updateNameState,
            {
                updateCharacterUseCase(character.id, newName, character.story, character.imageUrl)
                    .map { }
            }
        ) {
            setTableMemberCharacterUseCase(tableId, userId, character.id)
            getCharacterByIdUseCase(character.id).getOrNull()?.let { _refreshedCharacter.value = it }
        }
    }

    fun updateCharacterImage(character: Character, imageData: ImageData) {
        launchAsyncWithCallback(
            _updateImageState,
            {
                val imageUrl = uploadCharacterImageUseCase(userId, character.id, imageData)
                    .getOrElse { return@launchAsyncWithCallback Result.failure(it) }
                updateCharacterUseCase(character.id, character.name, character.story, imageUrl)
                    .map { }
            },
            // L'onglet n'affiche que la progression : l'échec (image trop lourde, illisible…)
            // passe par le snackbar
            onFailure = errorNotifier::notify
        ) {
            touchMemberCharacterUseCase(tableId, userId)
            getCharacterByIdUseCase(character.id).getOrNull()?.let { _refreshedCharacter.value = it }
        }
    }

    fun resetUpdateNameState() = resetState(_updateNameState)
    fun resetUpdateImageState() = resetState(_updateImageState)

    // --- Fiche de caractéristiques (mode construction libre) ---

    /** Enregistre la fiche complète en un seul écrit (clic sur « Terminer » de l'éditeur). */
    fun saveSheet(character: Character, sheet: CharacterSheet) = persistSheet(character, sheet)

    fun incrementGauge(character: Character, gaugeId: String) = stepGauge(character, gaugeId, up = true)

    fun decrementGauge(character: Character, gaugeId: String) = stepGauge(character, gaugeId, up = false)

    private fun stepGauge(character: Character, gaugeId: String, up: Boolean) {
        val gauges = character.sheet.gauges.map { gauge ->
            if (gauge.id != gaugeId) return@map gauge
            val delta = if (up) gauge.increment else -gauge.increment
            gauge.copy(current = (gauge.current + delta).coerceIn(gauge.min, gauge.max))
        }
        persistSheet(character, character.sheet.copy(gauges = gauges))
    }

    private fun persistSheet(character: Character, sheet: CharacterSheet) {
        launchAsyncWithCallback(
            _updateSheetState,
            { updateCharacterSheetUseCase(character.id, sheet).map { } }
        ) {
            getCharacterByIdUseCase(character.id).getOrNull()?.let {
                _refreshedCharacter.value = it.copy(signedImageUrl = character.signedImageUrl)
            }
        }
    }

    fun resetUpdateSheetState() = resetState(_updateSheetState)
}

/** Ce que le joueur peut choisir sur une table où il n'a pas encore de personnage. */
data class CharacterChoiceAvailability(
    val hasExistingCharacters: Boolean,
    val hasPreMades: Boolean
)

/**
 * Vrai si une requête est déjà en vol ou a déjà abouti — sert à rendre les chargements
 * idempotents, l'écran pouvant les redéclencher à chaque recomposition.
 *
 * Les chargeurs passent l'état à [AsyncUiState.Loading] avant de lancer leur coroutine, sans quoi
 * deux appels émis dans la même frame franchiraient tous les deux cette garde, et un nouvel essai
 * après échec réafficherait brièvement l'erreur précédente.
 */
private val AsyncUiState<*>.isPendingOrLoaded: Boolean
    get() = isLoading || isSuccess
